import apiClient from '@/lib/axios';
import type {
  JobListItemDto,
  JobDetailDto,
  JobFormData,
  JobStatusUpdateDto,
  JobsQueryParams,
  ApiResponse,
  SearchJobsResponse,
  JobCommissionBreakdownDto,
  CreateJobFromLeadCommand,
} from '../types';

/**
 * Transforms form data for API submission.
 * Converts empty strings to null for optional fields.
 */
const prepareJobDataForApi = (data: JobFormData): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    // Convert empty strings to null for optional fields
    if (value === '') {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Get list of jobs with optional filters
 */
export const getJobs = async (params?: JobsQueryParams): Promise<JobListItemDto[]> => {
  const response = await apiClient.get<SearchJobsResponse>('/jobs', { params });
  return response.data.items;
};

/**
 * Get a single job by ID with full details
 */
export const getJob = async (id: number): Promise<JobDetailDto> => {
  const response = await apiClient.get<JobDetailDto>(`/jobs/${id}`);
  return response.data;
};

/**
 * Get all jobs for a specific lead
 */
export const getJobsByLeadId = async (leadId: number, signal?: AbortSignal): Promise<JobListItemDto[]> => {
  const response = await apiClient.get<JobListItemDto[]>(`/jobs/by-lead/${leadId}`, { signal });
  return response.data;
};

/**
 * Update an existing job (description and notes)
 */
export const updateJob = async (id: number, data: JobFormData): Promise<JobDetailDto> => {
  const apiData = prepareJobDataForApi(data);
  const response = await apiClient.put<ApiResponse<JobDetailDto>>(`/jobs/${id}`, apiData);
  return response.data.data;
};

/**
 * Update job status with optional closing details
 */
export const updateJobStatus = async (
  id: number,
  statusUpdate: JobStatusUpdateDto
): Promise<JobDetailDto> => {
  const response = await apiClient.put<ApiResponse<JobDetailDto>>(
    `/jobs/${id}/status`,
    statusUpdate
  );
  return response.data.data;
};

/**
 * Create a new job from an existing lead
 */
export const createJobFromLead = async (
  leadId: number,
  command: CreateJobFromLeadCommand
): Promise<JobDetailDto> => {
  const response = await apiClient.post<ApiResponse<JobDetailDto>>(
    `/jobs/from-lead/${leadId}`,
    command
  );
  return response.data.data;
};

/**
 * Get commission breakdown for a job
 * Requires commissions:read permission
 */
export const getJobCommissionBreakdown = async (
  jobId: number
): Promise<JobCommissionBreakdownDto> => {
  const response = await apiClient.get<{ data: JobCommissionBreakdownDto }>(
    `/jobs/${jobId}/commission-breakdown`
  );
  return response.data.data;
};
